import React, { useState, useEffect, useMemo } from 'react';
import AppLayout from '../components/AppLayout';
import PersonalInfoEditModal from '../components/PersonalInfoEditModal';
import AthleticInfoEditModal from '../components/AthleticInfoEditModal';
import PerformanceMetricsEditModal from '../components/PerformanceMetricsEditModal';
import TeamInfoEditModal from '../components/TeamInfoEditModal';
import AcademicInfoEditModal from '../components/AcademicInfoEditModal';
import AwardsAndHonorsEditModal from '../components/AwardsAndHonorsEditModal';
import ProfileEditModal, { ProfileSection } from '../components/ProfileEditModal';
import TournamentSection from '../components/TournamentSection';
import AthleteDocumentsSection from '../components/AthleteDocumentsSection';
import ShareSettingsModal from '../components/ShareSettingsModal';
import CalendarModal from '../components/CalendarModal';
import CampsModal from '../components/CampsModal';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useResponsive } from '../hooks/useResponsive';
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard';
import SectionCompletenessPill from '../components/SectionCompletenessPill';
import { SectionKey } from '../hooks/useProfileCompleteness';
import { userAPI, athleteAPI, familyAPI } from '../services/api';
import { swrGet, swrSet } from '../utils/swrCache';
import { formatGpa } from '../utils/gpa';
import { Share2, User, MapPin, Calendar, Mail, Phone, Award, BookOpen, TrendingUp, Activity, Edit2, Camera, RefreshCw, ChevronRight, X } from 'react-feather';
import imageCompression from 'browser-image-compression';
import ErrorState from '../components/ErrorState';
import { BACKEND_CONFIG } from '../config/backendConfig';

interface AthleteData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  date_of_birth?: string;
  city: string;
  state: string;
  graduation_year: number;
  ncaa_id?: string;
  x_handle?: string | null;
  instagram_handle?: string | null;
  /* APS-259. The highlight video link (YouTube). */
  featured_video_url?: string | null;
  sport: string;
  position: string;
  bats?: string;
  throws?: string;
  height_inches?: number;
  weight_lbs?: number;
  current_gpa?: number;
  sat_total?: number;
  act_total?: number;
  awards?: string[];
  achievements?: string[];
  stats?: {
    home_to_1st?: number;
    sixty_yard_time?: number;
    forty_yard_time?: number;
    twenty_yard_time?: number;
    ten_yard_time?: number;
    pop_time?: number;
    grip_strength?: number;
    exit_velocity?: number;
    overhand_velocity?: number;
    pitch_velocities?: Array<{ pitch_type: string; value: number }>;
    spin_rates?: Array<{ pitch_type: string; value: number }>;
  };
  school_info?: {
    name?: string;
    coachingStaff?: Array<{
      name: string;
      email: string;
      phone?: string;
      sport?: string;
      role?: string;
    }>;
  };
  travel_club_info?: {
    name?: string;
    coachingStaff?: Array<{
      name: string;
      email: string;
      phone?: string;
      teamLevel?: string;
      role?: string;
    }>;
  };
  profile_picture_url?: string;
}

// Pitcher positions to detect
const PITCHER_POSITIONS = ['Pitcher', 'Relief Pitcher', 'Starting Pitcher', 'Starter/Reliever'];

const PlayerProfile: React.FC = () => {
  const { sessionToken, logout, user, setUser } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const { requirePaid } = useSubscriptionGuard();
  const [athleteData, setAthleteData] = useState<AthleteData | null>(null);
  // P2.1a — first-visit JIT prompt for missing critical profile fields.
  // Shown once per session per athlete (sessionStorage guard in the
  // ProfileCompletionPrompt component); skip + save both clear it.
  const [loading, setLoading] = useState(true);
  // #175 — distinguish a genuine profile-load FAILURE from the truly-empty
  // "guardian has no athletes yet" case. They must render differently.
  const [loadError, setLoadError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // One unified profile editor (ProfileEditModal) hosts all six sections as
  // tabs — no more opening/closing half a dozen separate modals. `null` = the
  // editor is closed; a section key = open on that tab.
  const [profileEditSection, setProfileEditSection] = useState<ProfileSection | null>(null);
  const [shareSettingsModalOpen, setShareSettingsModalOpen] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [campsModalOpen, setCampsModalOpen] = useState(false);
  const [coachesModalOpen, setCoachesModalOpen] = useState(false);
  const [selectedCoaches, setSelectedCoaches] = useState<any[]>([]);
  const [coachesModalTitle, setCoachesModalTitle] = useState('');
  const [detailedAcademicData, setDetailedAcademicData] = useState<any>(null);
  
  // Pitcher detection state
  const [isPitcher, setIsPitcher] = useState(false);
  
  // Athlete switching state (for guardians)
  const [athleteSwitchModalOpen, setAthleteSwitchModalOpen] = useState(false);
  const [familyAthletes, setFamilyAthletes] = useState<any[]>([]);
  const [currentUserType, setCurrentUserType] = useState<string>('');
  const [isSwitchingAthlete, setIsSwitchingAthlete] = useState(false);

  const loadExistingProfilePicture = async (athleteId: string) => {
    if (!sessionToken || !athleteId) return;
    
    try {
      const response = await athleteAPI.getProfilePicture(sessionToken, athleteId);
      
      if (response.success && response.data?.profilePictureUrl) {
        let imageUrl: string | null = null;
        
        if (response.data.thumbnails?.medium) {
          imageUrl = response.data.thumbnails.medium;
        } else {
          imageUrl = response.data.profilePictureUrl;
        }
        
        // Update athlete data with the loaded profile picture URL
        setAthleteData((prev: any) => ({
          ...prev,
          profile_picture_url: imageUrl
        }));
      }
    } catch (error) {
      console.error('❌ Failed to load existing profile picture:', error);
    }
  };

  const handleProfilePictureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !athleteData?.id || !sessionToken) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.warning('Please select an image file');
      return;
    }

    // Validate file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      toast.warning('Image size must be less than 10MB');
      return;
    }

    setIsUploading(true);
    try {
      // Compress image before upload (matching mobile: 800x800, quality 0.8)
      console.log('🗜️ Compressing profile picture...');
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        initialQuality: 0.8,
      });
      
      console.log('Original file size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
      console.log('Compressed file size:', (compressedFile.size / 1024 / 1024).toFixed(2), 'MB');

      // Create FormData for web upload
      const formData = new FormData();
      formData.append('profilePicture', compressedFile);

      const url = `${BACKEND_CONFIG.baseURL}/athletes/${athleteData.id}/profile-picture`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const responseData = await response.json();

      if (responseData.success) {
        let imageUrl: string | null = null;
        
        if (responseData.data.thumbnails?.medium) {
          imageUrl = responseData.data.thumbnails.medium;
        } else if (responseData.data.profilePictureUrl) {
          imageUrl = responseData.data.profilePictureUrl;
        }
        
        // Update athlete data with new profile picture URL
        setAthleteData((prev: any) => ({
          ...prev,
          profile_picture_url: imageUrl
        }));
        
        toast.success('Profile picture updated successfully!');
      } else {
        toast.error('Failed to upload profile picture. Please try again.');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error('Failed to upload profile picture. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset the input so the same file can be selected again if needed
      event.target.value = '';
    }
  };

  const loadAthleteProfile = async () => {
    if (!sessionToken) {
      setLoading(false);
      return;
    }

    setLoadError(false);
    try {
      // audit #147: these three are independent, so fire them together instead
      // of the old sequential waterfall (comprehensive → getProfile → my-athletes).
      // - getComprehensiveProfile / getProfile are load-critical (a real failure
      //   should surface the retry state), so they reject the Promise.all.
      // - my-athletes only decides WHICH athlete to show; its failure falls back
      //   to the first comprehensive athlete, so it can't fail the load.
      const [profileData, userProfile, myAthletesRes] = await Promise.all([
        userAPI.getComprehensiveProfile(sessionToken),
        userAPI.getProfile(sessionToken),
        familyAPI.getMyAthletes(sessionToken).catch((e) => {
          console.warn('my-athletes lookup failed, will fall back to first athlete:', e);
          return null;
        }),
      ]);

      // Stash user_type for the existing UI conditionals that key off it.
      setCurrentUserType(userProfile.user_type || '');

      // Guardian family-athlete hydration (per-athlete profile pics) is secondary
      // N+1 data for the athlete switcher — fire it but don't block the body on it.
      loadFamilyAthletes(profileData);

      // Which athlete is this user responsible for? Prefer the my-athletes id
      // (correct for guardians via guardian_athlete_relationships). Seed it
      // instantly from the shared myAthletes:first SWR cache when the fresh call
      // hasn't returned an id, and refresh that cache when it has.
      const firstMine = myAthletesRes?.athletes?.[0];
      if (firstMine?.id) swrSet('myAthletes:first', firstMine);
      const cachedFirst = swrGet<{ id: string }>('myAthletes:first', 10 * 60 * 1000);
      const myId = firstMine?.id || cachedFirst?.id;

      let currentAthlete = null;
      if (myId && profileData?.athletes?.length) {
        currentAthlete = profileData.athletes.find(a => a.id === myId) || null;
      }

      // Fallback: first athlete on the comprehensive profile (legacy behavior
      // for edge cases where my-athletes returns nothing, e.g. unmigrated
      // legacy athletes whose users row has athlete_profile_id but no
      // guardian_athlete_relationships row exists yet).
      if (!currentAthlete && profileData?.athletes?.[0]) {
        currentAthlete = profileData.athletes[0];
      }

      if (currentAthlete) {
        const profile = currentAthlete;

        // Transform exactly like mobile does
        const transformedData: AthleteData = {
          id: profile.id,
          first_name: profile.firstName,
          last_name: profile.lastName,
          email: profile.email,
          phone_number: profile.phone,
          date_of_birth: profile.dateOfBirth,
          city: profile.city,
          state: profile.state,
          graduation_year: profile.graduationYear,
          ncaa_id: profile.ncaaId,
          x_handle: profile.xHandle,
          instagram_handle: profile.instagramHandle,
          // /profiles/me may not carry the video link; my-athletes does.
          featured_video_url: (profile as any).featuredVideoUrl
            ?? (profile as any).featured_video_url
            ?? myAthletesRes?.athletes?.find((a) => a.id === profile.id)?.featured_video_url
            ?? null,
          sport: profile.sportInfo?.sport || profile.sport,
          position: Array.isArray(profile.sportInfo?.positions) 
            ? profile.sportInfo.positions.join(', ') 
            : profile.sportInfo?.positions || profile.position,
          bats: profile.sportInfo?.bats,
          throws: profile.sportInfo?.throws,
          height_inches: typeof profile.sportInfo?.height === 'string' 
            ? parseHeightToInches(profile.sportInfo.height) || undefined
            : profile.sportInfo?.height,
          weight_lbs: profile.sportInfo?.weight,
          awards: profile.sportInfo?.awards || [],
          current_gpa: profile.academicPerformance?.currentGpa,
          sat_total: profile.academicPerformance?.satScores?.total,
          act_total: profile.academicPerformance?.actScores?.total,
          achievements: (profile.academicPerformance as any)?.achievements || [],
          stats: {
            home_to_1st: profile.performanceMetrics?.speed?.homeToFirst,
            sixty_yard_time: profile.performanceMetrics?.speed?.sixtyYardTime,
            forty_yard_time: profile.performanceMetrics?.speed?.fortyYardTime,
            twenty_yard_time: profile.performanceMetrics?.speed?.twentyYardTime,
            ten_yard_time: profile.performanceMetrics?.speed?.tenYardTime,
            pop_time: profile.performanceMetrics?.catching?.popTime,
            grip_strength: profile.performanceMetrics?.strength?.gripStrength,
            exit_velocity: profile.performanceMetrics?.strength?.exitVelocity,
            overhand_velocity: profile.performanceMetrics?.strength?.overhandVelocity,
          },
          school_info: (profile as any).schoolInfo?.school,
          travel_club_info: (profile as any).schoolInfo?.travelClub,
          profile_picture_url: undefined,
        };
        
        setAthleteData(transformedData);
        
        // Check if athlete is a pitcher based on positions
        const positions = Array.isArray(profile.sportInfo?.positions) 
          ? profile.sportInfo.positions 
          : profile.sportInfo?.positions ? [profile.sportInfo.positions] : [];
        const athleteIsPitcher = positions.some((pos: string) => PITCHER_POSITIONS.includes(pos));
        setIsPitcher(athleteIsPitcher);
        
        // audit #147: the body is now fully driven by transformedData, so render
        // it immediately and let these secondary fetches (profile picture,
        // academic achievements, pitcher pitch metrics) resolve in the
        // background instead of gating the spinner on all three in series.
        Promise.all([
          loadExistingProfilePicture(profile.id),
          loadInitialAcademicAchievements(profile.id),
          loadPitchMetrics(profile.id),
        ]).catch((e) => {
          console.warn('secondary profile data load failed:', e);
        });
      }
    } catch (error) {
      // #175 — a real fetch failure is NOT the same as "no athletes"; flag it so
      // the render shows a retry surface instead of the empty-state CTA.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  // Load family athletes for guardian users
  const loadFamilyAthletes = async (profileData: any) => {
    try {
      // If user is a guardian, load all family athletes
      if (profileData?.athletes) {
        // Load profile pictures for each athlete
        const athletesWithProfilePics = await Promise.all(
          profileData.athletes.map(async (athlete: any) => {
            try {
              const response = await athleteAPI.getProfilePicture(sessionToken!, athlete.id);
              if (response.success && response.data?.profilePictureUrl) {
                return {
                  ...athlete,
                  profilePictureUrl: response.data.thumbnails?.medium || response.data.profilePictureUrl
                };
              }
            } catch (error) {
              console.log(`No profile picture found for athlete ${athlete.id}`);
            }
            return athlete;
          })
        );
        
        setFamilyAthletes(athletesWithProfilePics);
      }
    } catch (error) {
      console.error('Error loading family athletes:', error);
    }
  };

  // Switch to different athlete profile
  const switchAthleteProfile = async (athleteId: string) => {
    if (!sessionToken) return;
    
    setIsSwitchingAthlete(true);
    try {
      // Update user's athlete_profile_id
      const userProfile = await userAPI.getProfile(sessionToken);

      await familyAPI.updateMemberProfileIds(
        sessionToken,
        userProfile.id,
        { athlete_profile_id: athleteId }
      );

      // Push the new athlete_profile_id into AuthContext so any consumer
      // reading user.athlete_profile_id (Dashboard, Messages, guards)
      // immediately reflects the switch. Without this the global user
      // stays stale until the next sign-in.
      if (user) {
        setUser({ ...(user as any), athlete_profile_id: athleteId } as any);
      }

      // Reload the profile with the new athlete. The backend's
      // /me/my-athletes endpoint now orders by users.athlete_profile_id
      // first, so loadAthleteProfile picks the newly-switched athlete.
      await loadAthleteProfile();

      setAthleteSwitchModalOpen(false);

      toast.success('Switched athlete profile successfully!');
    } catch (error) {
      console.error('Error switching athlete profile:', error);
      toast.error('Failed to switch athlete profile. Please try again.');
    } finally {
      setIsSwitchingAthlete(false);
    }
  };

  useEffect(() => {
    loadAthleteProfile();
  }, [sessionToken]);

  // JIT router killed — per-section completeness pills on each card header
  // now surface the same info inline, no modal needed.

  // Phase 2.5 — deep-link from Dashboard / College Lists banners. Open
  // the requested edit modal once athlete data lands, then strip the param
  // so a refresh doesn't re-open.
  useEffect(() => {
    if (!athleteData) return;
    const params = new URLSearchParams(window.location.search);
    const target = params.get('openModal') as SectionKey | null;
    if (!target) return;
    const opener: Record<SectionKey, () => void> = {
      personal: () => openProfileEdit('personal'),
      athletic: () => openProfileEdit('athletic'),
      performance: () => openProfileEdit('performance'),
      team: () => openProfileEdit('team'),
      academic: () => openProfileEdit('academic'),
      // A file picker needs the user's own tap. Point at the photo control.
      image: () => {
        const el = document.getElementById('profile-picture-control');
        if (!el) return;
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
        try { el.focus({ preventScroll: true }); } catch {}
        el.style.outline = '2px solid var(--aps-action)';
        el.style.outlineOffset = '3px';
        window.setTimeout(() => { el.style.outline = ''; el.style.outlineOffset = ''; }, 2400);
      },
    };
    const fn = opener[target];
    if (fn) {
      fn();
      params.delete('openModal');
      const search = params.toString();
      const url = window.location.pathname + (search ? `?${search}` : '') + window.location.hash;
      try { window.history.replaceState({}, '', url); } catch {}
    }
  }, [athleteData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const parseHeightToInches = (heightString: string): number | null => {
    if (!heightString) return null;
    const match = heightString.match(/^(\d+)'(\d+)"?$/);
    if (match) {
      const feet = parseInt(match[1]);
      const inches = parseInt(match[2]);
      return feet * 12 + inches;
    }
    return null;
  };

  const formatHeight = (inches?: number | null): string => {
    if (!inches || isNaN(inches)) return 'Not set';
    const feet = Math.floor(inches / 12);
    const remainingInches = Math.round(inches % 12);
    return `${feet}'${remainingInches}"`;
  };

  const getHeadCoach = (coaches?: Array<any>) => {
    if (!coaches || coaches.length === 0) return null;
    return coaches.find(c => c.role?.toLowerCase() === 'head coach') || coaches[0];
  };

  const openCoachesModal = (coachingStaff: any[], title: string) => {
    setSelectedCoaches(coachingStaff);
    setCoachesModalTitle(title);
    setCoachesModalOpen(true);
  };

  // Load academic achievements during initial profile load (matches mobile pattern)
  const loadInitialAcademicAchievements = async (athleteId: string) => {
    if (!sessionToken) return;
    
    try {
      const response = await athleteAPI.getAcademicPerformance(sessionToken, athleteId);
      if (response?.success && response?.data?.academicPerformance?.achievements) {
        setAthleteData((prev: any) => ({
          ...prev,
          achievements: response.data.academicPerformance.achievements
        }));
      }
    } catch (error) {
      console.error('Error loading initial academic achievements:', error);
      // Don't show error to user - just continue without achievements
    }
  };

  // Load pitch metrics (velocity and spin rate) for pitchers
  const loadPitchMetrics = async (athleteId: string) => {
    if (!sessionToken) return;
    
    try {
      const response = await athleteAPI.getPerformanceMetrics(sessionToken, athleteId);
      if (response?.success && response?.data?.metrics) {
        const { pitch_velocities, spin_rates } = response.data.metrics;
        if ((pitch_velocities && pitch_velocities.length > 0) || (spin_rates && spin_rates.length > 0)) {
          setAthleteData((prev: any) => ({
            ...prev,
            stats: {
              ...prev?.stats,
              pitch_velocities: pitch_velocities || [],
              spin_rates: spin_rates || [],
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error loading pitch metrics:', error);
      // Don't show error to user - just continue without pitch data
    }
  };

  const loadDetailedAcademicData = async () => {
    if (!sessionToken || !athleteData?.id) return;
    
    try {
      const response = await athleteAPI.getAcademicPerformance(sessionToken, athleteData.id);
      if (response?.success && response?.data?.academicPerformance) {
        setDetailedAcademicData(response.data.academicPerformance);
      } else {
        // No existing data yet — open with an empty (but valid) form.
        setDetailedAcademicData({});
      }
    } catch (error) {
      // #164 — a genuine fetch failure must NOT open the modal with blank
      // fields, or Save would post blanks over real SAT/ACT/GPA. Surface the
      // error and keep the academic tab in its loading state instead.
      toast.error('Could not load academic information. Please try again.');
    }
  };

  // Open the unified profile editor on a given section (used by every section's
  // Edit pencil and the deep-link opener). The academic tab lazy-loads its
  // detailed data the first time it's opened.
  const openProfileEdit = (section: ProfileSection) => {
    setProfileEditSection(section);
    if (section === 'academic' && detailedAcademicData === null) {
      loadDetailedAcademicData();
    }
  };

  const closeProfileEdit = () => {
    setProfileEditSection(null);
    setDetailedAcademicData(null);
  };

  const formatPosition = (position?: string | null): string => {
    if (!position) return 'Position';
    if (typeof position === 'string') {
      const firstPos = position.split(',')[0].trim();
      return firstPos || 'Position';
    }
    return 'Position';
  };

  const athleticInitialData = useMemo(() => athleteData ? {
    id: athleteData.id,
    sport: athleteData.sport,
    position: athleteData.position,
    height_inches: athleteData.height_inches,
    weight_lbs: athleteData.weight_lbs,
    bats: athleteData.bats,
    throws: athleteData.throws,
    awards: athleteData.awards || []
  } : { id: '', sport: '', position: '', awards: [] }, [athleteData]);

  const teamInitialData = useMemo(() => athleteData ? {
    school_info: athleteData.school_info,
    travel_club_info: athleteData.travel_club_info,
  } : {}, [athleteData]);

  if (loading) {
    return (
      <AppLayout>
        <div style={{ 
          padding: '64px', 
          textAlign: 'center',
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ fontSize: '16px', color: '#64748b', fontFamily: 'Manrope, sans-serif' }}>
            Loading profile...
          </div>
        </div>
      </AppLayout>
    );
  }

  // #175 — genuine load failure gets a retry surface...
  if (loadError && !athleteData) {
    return (
      <AppLayout>
        <div style={{ padding: '48px 24px' }}>
          <ErrorState
            message="We couldn't load this profile. Please check your connection and try again."
            onRetry={() => { setLoading(true); loadAthleteProfile(); }}
          />
        </div>
      </AppLayout>
    );
  }

  // ...while the truly-empty (no athlete yet) case keeps the add-athlete CTA.
  if (!athleteData) {
    return (
      <AppLayout>
        <div style={{ padding: '64px', textAlign: 'center' }}>
          <div style={{ fontSize: '16px', color: '#64748b', marginBottom: '16px' }}>No athlete profile found</div>
          <button
            onClick={() => { window.location.href = '/onboarding/quickStart'; }}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              color: 'white',
              background: '#F9550D',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            Add an athlete
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <>
    <AppLayout>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{
        background: '#F9FAFB',
        minHeight: 'calc(100vh - 80px)',
        padding: isMobile ? '16px' : '32px 24px',
        fontFamily: 'Manrope, sans-serif'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          
          {/* Profile Header */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: isMobile ? '16px' : '24px',
            marginBottom: isMobile ? '16px' : '24px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ 
              display: 'flex', 
              gap: isMobile ? '16px' : '24px', 
              alignItems: 'flex-start',
              flexDirection: isMobile ? 'column' : 'row',
            }}>
              {/* Profile Picture */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div
                  id="profile-picture-control"
                  role="button"
                  tabIndex={0}
                  aria-label="Upload profile picture"
                  style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '12px',
                  background: '#F3F4F6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  fontWeight: '600',
                  color: '#9CA3AF',
                  overflow: 'hidden',
                  position: 'relative',
                  cursor: 'pointer'
                }}
                onClick={() => document.getElementById('profile-picture-input')?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    document.getElementById('profile-picture-input')?.click();
                  }
                }}
                >
                  {isUploading ? (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      background: 'rgba(0,0,0,0.5)'
                    }}>
                      <div style={{ 
                        border: '3px solid #f3f3f3',
                        borderTop: '3px solid #F9550D',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        animation: 'spin 1s linear infinite'
                      }} />
                    </div>
                  ) : athleteData.profile_picture_url ? (
                    <>
                      <img 
                        src={athleteData.profile_picture_url} 
                        alt="Profile" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {/* Edit Overlay */}
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'rgba(0,0,0,0.6)',
                        padding: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
                      >
                        <Edit2 size={14} color="white" />
                      </div>
                    </>
                  ) : (
                    <>
                      <Camera size={32} color="#9CA3AF" />
                      {/* Edit Overlay */}
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'rgba(0,0,0,0.6)',
                        padding: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
                      >
                        <Edit2 size={14} color="white" />
                      </div>
                    </>
                  )}
                </div>
                {/* Hidden file input */}
                <input
                  id="profile-picture-input"
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  style={{ display: 'none' }}
                />
              </div>

              {/* Profile Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px', flexWrap: 'wrap' }}>
                  <h1 style={{
                    fontSize: isMobile ? '20px' : '24px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: 0
                  }}>
                    {athleteData.first_name} {athleteData.last_name}
                  </h1>
                  
                  {/* Switch Button - Only show for guardians with multiple athletes */}
                  {currentUserType === 'guardian' && familyAthletes.length > 1 && (
                    <button
                      onClick={() => setAthleteSwitchModalOpen(true)}
                      disabled={isSwitchingAthlete}
                      title="Switch Athlete"
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '16px',
                        background: 'rgba(249, 85, 13, 0.1)',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isSwitchingAthlete ? 'default' : 'pointer',
                        flexShrink: 0,
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => !isSwitchingAthlete && (e.currentTarget.style.background = 'rgba(249, 85, 13, 0.2)')}
                      onMouseLeave={(e) => !isSwitchingAthlete && (e.currentTarget.style.background = 'rgba(249, 85, 13, 0.1)')}
                    >
                      {isSwitchingAthlete ? (
                        <div style={{ 
                          border: '2px solid rgba(249, 85, 13, 0.3)',
                          borderTop: '2px solid #F9550D',
                          borderRadius: '50%',
                          width: '16px',
                          height: '16px',
                          animation: 'spin 1s linear infinite'
                        }} />
                      ) : (
                        <RefreshCw size={16} color="#F9550D" />
                      )}
                    </button>
                  )}
                </div>
                <div style={{
                  fontSize: '16px',
                  color: '#6B7280',
                  marginBottom: '8px'
                }}>
                  {formatPosition(athleteData.position)} • Class of {athleteData.graduation_year}
                </div>
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  fontSize: '14px',
                  color: '#6B7280'
                }}>
                  <div>{athleteData.city}, {athleteData.state}</div>
                  {athleteData.bats && athleteData.throws && (
                    <div>
                      {athleteData.bats.charAt(0).toUpperCase()}/{athleteData.throws.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              </div>

              {/* Button Bank */}
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                gap: isMobile ? '8px' : '12px',
                alignItems: isMobile ? 'stretch' : 'flex-end',
                justifyContent: isMobile ? 'stretch' : 'center',
                width: isMobile ? '100%' : 'auto',
              }}>
                {/* Calendar Button */}
                <button
                  onClick={() => requirePaid('Recruiting Calendar', () => setCalendarModalOpen(true))}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: isMobile ? '10px 12px' : '12px 16px',
                    background: '#FFFFFF',
                    color: '#F9550D',
                    border: '2px solid #F9550D',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontFamily: 'Manrope, sans-serif',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    flex: isMobile ? 1 : 'none',
                    minWidth: isMobile ? 'auto' : '90px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F9550D';
                    e.currentTarget.style.color = '#FFFFFF';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 85, 13, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#FFFFFF';
                    e.currentTarget.style.color = '#F9550D';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                  }}
                >
                  <Calendar size={20} strokeWidth={2.5} />
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    letterSpacing: '0.3px',
                    fontFamily: 'Manrope, sans-serif'
                  }}>
                    Recruiting
                  </span>
                </button>

                {/* Camps Button */}
                <button
                  onClick={() => requirePaid('Camps', () => setCampsModalOpen(true))}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: isMobile ? '10px 12px' : '12px 16px',
                    background: '#FFFFFF',
                    color: '#F9550D',
                    border: '2px solid #F9550D',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontFamily: 'Manrope, sans-serif',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    flex: isMobile ? 1 : 'none',
                    minWidth: isMobile ? 'auto' : '90px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F9550D';
                    e.currentTarget.style.color = '#FFFFFF';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 85, 13, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#FFFFFF';
                    e.currentTarget.style.color = '#F9550D';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                  }}
                >
                  <MapPin size={20} strokeWidth={2.5} />
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    letterSpacing: '0.3px',
                    fontFamily: 'Manrope, sans-serif'
                  }}>
                    Camps
                  </span>
                </button>

                {/* Share Profile Button */}
                <button
                  onClick={() => requirePaid('Share Profile', () => setShareSettingsModalOpen(true))}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: isMobile ? '10px 12px' : '12px 16px',
                    background: '#FFFFFF',
                    color: '#F9550D',
                    border: '2px solid #F9550D',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontFamily: 'Manrope, sans-serif',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    flex: isMobile ? 1 : 'none',
                    minWidth: isMobile ? 'auto' : '90px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F9550D';
                    e.currentTarget.style.color = '#FFFFFF';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 85, 13, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#FFFFFF';
                    e.currentTarget.style.color = '#F9550D';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                  }}
                >
                  <Share2 size={20} strokeWidth={2.5} />
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    letterSpacing: '0.3px',
                    fontFamily: 'Manrope, sans-serif'
                  }}>
                    Share Profile
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Tournament Schedule Section - Full Width */}
          <div style={{ marginTop: isMobile ? '16px' : '24px' }}>
            <TournamentSection athleteId={athleteData.id} />
          </div>

          {/* Content Grid - 2 columns on desktop, 1 on mobile */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))',
            gap: isMobile ? '16px' : '24px',
            marginTop: isMobile ? '16px' : '24px'
          }}>
            
            {/* Personal Information */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <User size={20} color="#F9550D" />
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: 0
                  }}>
                    Personal Information
                  </h3>
                  {athleteData && (
                    <SectionCompletenessPill
                      athlete={athleteData as any}
                      section="personal"
                      onPress={() => openProfileEdit('personal')}
                    />
                  )}
                </div>
                <button
                  title="Edit Personal Information"
                  onClick={() => openProfileEdit('personal')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#F9550D',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'Manrope, sans-serif',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#FEF2F2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Edit2 size={16} />
                </button>
              </div>
              <div>
                {/* "Athlete X" prefix on each row — guardians editing
                    a profile shouldn't read these as their own info. */}
                <InfoRow label="Athlete Name" value={`${athleteData.first_name} ${athleteData.last_name}`} />
                <InfoRow label="Athlete Email" value={athleteData.email} />
                <InfoRow label="Athlete Phone" value={athleteData.phone_number || 'Not provided'} />
                <InfoRow
                  label="Athlete Date of Birth"
                  value={athleteData.date_of_birth ? (() => { const [y, m, d] = athleteData.date_of_birth.substring(0, 10).split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString(); })() : 'Not provided'}
                />
                {athleteData.ncaa_id && (
                  <InfoRow label="Athlete NCAA ID#" value={athleteData.ncaa_id} />
                )}
                {/* APS-255. Shown as @handle, the form the athlete recognises;
                    stored without the @. A row appears only when there is one,
                    exactly like the NCAA ID above, so a profile with no handles
                    is unchanged. */}
                {athleteData.x_handle && (
                  <InfoRow label="Athlete X Handle" value={`@${athleteData.x_handle}`} />
                )}
                {athleteData.instagram_handle && (
                  <InfoRow label="Athlete Instagram Handle" value={`@${athleteData.instagram_handle}`} />
                )}
                {athleteData.featured_video_url && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                    paddingTop: '8px',
                    paddingBottom: '8px'
                  }}>
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>Highlight Video</span>
                    <a
                      href={athleteData.featured_video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '14px', fontWeight: 600, color: 'var(--aps-action-on-tint)', textAlign: 'right', wordBreak: 'break-all' }}
                    >
                      Watch on YouTube
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Athletic Information */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TrendingUp size={20} color="#F9550D" />
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: 0
                  }}>
                    Athletic Information
                  </h3>
                  {athleteData && (
                    <SectionCompletenessPill
                      athlete={athleteData as any}
                      section="athletic"
                      onPress={() => openProfileEdit('athletic')}
                    />
                  )}
                </div>
                <button
                  title="Edit Athletic Information"
                  onClick={() => openProfileEdit('athletic')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#F9550D',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'Manrope, sans-serif',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#FEF2F2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Edit2 size={16} />
                </button>
              </div>
              <div>
                <div style={{ display: 'flex', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>Sport</div>
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>{athleteData.sport}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>Position</div>
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>{athleteData.position}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>Height</div>
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>{formatHeight(athleteData.height_inches)}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>Weight</div>
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>
                      {athleteData.weight_lbs ? `${athleteData.weight_lbs} lbs` : 'Not set'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>Bats</div>
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>
                      {athleteData.bats ? athleteData.bats.charAt(0).toUpperCase() + athleteData.bats.slice(1) : 'Not set'}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>Throws</div>
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>
                      {athleteData.throws ? athleteData.throws.charAt(0).toUpperCase() + athleteData.throws.slice(1) : 'Not set'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Metrics - Separate Card */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Activity size={20} color="#F9550D" />
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: 0
                  }}>
                    Performance Metrics
                  </h3>
                  {athleteData && (
                    <SectionCompletenessPill
                      athlete={athleteData as any}
                      section="performance"
                      onPress={() => openProfileEdit('performance')}
                    />
                  )}
                </div>
                <button
                  title="Edit Performance Metrics"
                  onClick={() => openProfileEdit('performance')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#F9550D',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'Manrope, sans-serif',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#FEF2F2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Edit2 size={16} />
                </button>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '12px'
              }}>
                <MetricItem label="Home to 1st" value={athleteData.stats?.home_to_1st ? `${athleteData.stats.home_to_1st}s` : '--'} />
                <MetricItem label="5x10x5 Shuttle" value={athleteData.stats?.sixty_yard_time ? `${athleteData.stats.sixty_yard_time}s` : '--'} />
                <MetricItem label="40 Yard Time" value={athleteData.stats?.forty_yard_time ? `${athleteData.stats.forty_yard_time}s` : '--'} />
                <MetricItem label="20 Yard Time" value={athleteData.stats?.twenty_yard_time ? `${athleteData.stats.twenty_yard_time}s` : '--'} />
                <MetricItem label="10 Yard Time" value={athleteData.stats?.ten_yard_time ? `${athleteData.stats.ten_yard_time}s` : '--'} />
                <MetricItem label="Pop Time" value={athleteData.stats?.pop_time ? `${athleteData.stats.pop_time}s` : '--'} />
                <MetricItem label="Grip Strength" value={athleteData.stats?.grip_strength ? `${athleteData.stats.grip_strength} lbs` : '-- lbs'} />
                <MetricItem label="Exit Velocity" value={athleteData.stats?.exit_velocity ? `${athleteData.stats.exit_velocity} mph` : '-- mph'} />
                <MetricItem label="Overhand Velocity" value={athleteData.stats?.overhand_velocity ? `${athleteData.stats.overhand_velocity} mph` : '-- mph'} />
              </div>
              
              {/* Pitching Metrics Section - Only show if athlete has pitch data */}
              {(athleteData.stats?.pitch_velocities?.length || athleteData.stats?.spin_rates?.length) ? (
                <>
                  <div style={{
                    borderTop: '1px solid #E5E7EB',
                    margin: '16px 0',
                    paddingTop: '16px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <TrendingUp size={18} color="#F9550D" />
                      <h4 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#1F2937',
                        margin: 0
                      }}>
                        Pitching Metrics
                      </h4>
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '12px'
                    }}>
                      {/* Display pitch velocities */}
                      {athleteData.stats?.pitch_velocities?.map((pitch, index) => (
                        <MetricItem 
                          key={`velocity-${index}`}
                          label={`${pitch.pitch_type} Velocity`} 
                          value={`${pitch.value} mph`} 
                        />
                      ))}
                      {/* Display spin rates */}
                      {athleteData.stats?.spin_rates?.map((pitch, index) => (
                        <MetricItem 
                          key={`spin-${index}`}
                          label={`${pitch.pitch_type} Spin Rate`} 
                          value={`${pitch.value} rpm`} 
                        />
                      ))}
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            {/* Team Information - ALWAYS SHOW */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MapPin size={20} color="#F9550D" />
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: 0
                  }}>
                    Team Information
                  </h3>
                  {athleteData && (
                    <SectionCompletenessPill
                      athlete={athleteData as any}
                      section="team"
                      onPress={() => openProfileEdit('team')}
                    />
                  )}
                </div>
                <button
                  title="Edit Team Information"
                  onClick={() => openProfileEdit('team')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#F9550D',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'Manrope, sans-serif',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#FEF2F2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Edit2 size={16} />
                </button>
              </div>
              
              {/* High School Team */}
              {athleteData.school_info?.name ? (
                <div style={{ marginBottom: athleteData.travel_club_info?.name ? '16px' : '0' }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                    High School Team
                  </div>
                  <TeamInfo 
                    teamName={athleteData.school_info.name}
                    headCoach={getHeadCoach(athleteData.school_info.coachingStaff)}
                  />
                  {athleteData.school_info.coachingStaff && athleteData.school_info.coachingStaff.length > 1 && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => openCoachesModal(athleteData.school_info?.coachingStaff || [], 'High School Coaches')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openCoachesModal(athleteData.school_info?.coachingStaff || [], 'High School Coaches'); } }}
                      style={{
                        marginTop: '8px',
                        padding: '8px',
                        textAlign: 'center',
                        color: '#F9550D',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      See All Coaches <ChevronRight size={16} aria-hidden style={{ verticalAlign: 'text-bottom' }} />
                    </div>
                  )}
                </div>
              ) : null}

              {/* Travel/Club Team */}
              {athleteData.travel_club_info?.name ? (
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                    Travel/Club Team
                  </div>
                  <TeamInfo 
                    teamName={athleteData.travel_club_info.name}
                    headCoach={getHeadCoach(athleteData.travel_club_info.coachingStaff)}
                    teamLevel={getHeadCoach(athleteData.travel_club_info.coachingStaff)?.teamLevel}
                  />
                  {athleteData.travel_club_info.coachingStaff && athleteData.travel_club_info.coachingStaff.length > 1 && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => openCoachesModal(athleteData.travel_club_info?.coachingStaff || [], 'Travel/Club Coaches')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openCoachesModal(athleteData.travel_club_info?.coachingStaff || [], 'Travel/Club Coaches'); } }}
                      style={{
                        marginTop: '8px',
                        padding: '8px',
                        textAlign: 'center',
                        color: '#F9550D',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      See All Coaches <ChevronRight size={16} aria-hidden style={{ verticalAlign: 'text-bottom' }} />
                    </div>
                  )}
                </div>
              ) : null}

              {/* Placeholder if no team info */}
              {!athleteData.school_info?.name && !athleteData.travel_club_info?.name && (
                <div style={{ 
                  padding: '24px',
                  textAlign: 'center',
                  color: '#9CA3AF',
                  fontSize: '14px'
                }}>
                  <div style={{ marginBottom: '8px' }}>No team information yet</div>
                  <div style={{ fontSize: '13px' }}>Team details will appear here when added</div>
                </div>
              )}
            </div>

            {/* Academic Information */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BookOpen size={20} color="#F9550D" />
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: 0
                  }}>
                    Academic Information
                  </h3>
                  {athleteData && (
                    <SectionCompletenessPill
                      athlete={athleteData as any}
                      section="academic"
                      onPress={() => openProfileEdit('academic')}
                    />
                  )}
                </div>
                <button
                  onClick={() => openProfileEdit('academic')}
                  title="Edit Academic Information"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#F9550D',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'Manrope, sans-serif',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#FEF2F2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Edit2 size={16} />
                </button>
              </div>
              <div>
                <InfoRow label="GPA" value={formatGpa(athleteData.current_gpa) || 'Not provided'} />
                <InfoRow label="SAT Score" value={athleteData.sat_total?.toString() || 'Not taken'} />
                <InfoRow label="ACT Score" value={athleteData.act_total?.toString() || 'Not taken'} />
              </div>
            </div>

            {/* Awards & Honors */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #E5E7EB',
              gridColumn: 'span 1'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Award size={20} color="#F9550D" />
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: 0
                  }}>
                    Awards & Honors
                  </h3>
                </div>
                <button
                  title="Edit Awards & Honors"
                  onClick={() => openProfileEdit('awards')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#F9550D',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'Manrope, sans-serif',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#FEF2F2'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <Edit2 size={16} />
                </button>
              </div>
              
              {/* Athletic Awards */}
              {athleteData.awards && athleteData.awards.length > 0 && (
                <>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                    Athletic Awards
                  </div>
                  {athleteData.awards.map((award, index) => (
                    <div key={`athletic-${index}`} style={{
                      padding: '12px',
                      background: '#F9FAFB',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      fontSize: '14px',
                      color: '#1F2937'
                    }}>
                      {award}
                      <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                        Athletic Award
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Academic Achievements */}
              {athleteData.achievements && athleteData.achievements.length > 0 && (
                <>
                  {athleteData.awards && athleteData.awards.length > 0 && (
                    <div style={{ height: '1px', background: '#E5E7EB', margin: '16px 0' }} />
                  )}
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                    Academic Achievements
                  </div>
                  {athleteData.achievements.map((achievement, index) => (
                    <div key={`academic-${index}`} style={{
                      padding: '12px',
                      background: '#F9FAFB',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      fontSize: '14px',
                      color: '#1F2937'
                    }}>
                      {achievement}
                      <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                        Academic Achievement
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Placeholder if no awards */}
              {(!athleteData.awards || athleteData.awards.length === 0) && 
               (!athleteData.achievements || athleteData.achievements.length === 0) && (
                <div style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: '#9CA3AF',
                  fontSize: '14px'
                }}>
                  <div style={{ marginBottom: '8px', fontWeight: '600' }}>No awards yet</div>
                  <button
                    onClick={() => openProfileEdit('awards')}
                    style={{
                      marginTop: '4px',
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#F9550D',
                      background: 'white',
                      border: '1px solid rgba(249, 85, 13, 0.4)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    Add awards &amp; honors
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Documents - transcripts and schedule flyers (KGH-795 / APS-255).
              Full width under the grid rather than a seventh card inside it:
              this one is a place the family DOES something (pick a file, name
              it, decide who may open it), not a place they read a saved value,
              and an upload form with a progress bar in a 500px grid column is
              cramped at every width. It sits after the sections a document
              backs up - the transcript belongs to Academic Information, the
              flyer to the Tournament Schedule above - so a parent meets it
              having just seen the GPA it evidences. */}
          <div style={{ marginTop: isMobile ? '16px' : '24px' }}>
            <AthleteDocumentsSection
              athleteId={athleteData.id}
              athleteFirstName={athleteData.first_name}
            />
          </div>
        </div>
      </div>
    </AppLayout>

    {/* Unified profile editor — six sections as tabs (replaces six separate
        section modals). Only the active section is mounted; each renders in
        `embedded` mode and keeps its own form + Save. A successful save closes
        the editor and shows a toast. A failed save keeps it open. */}
    <ProfileEditModal
      activeSection={profileEditSection}
      onSelectSection={openProfileEdit}
      onClose={closeProfileEdit}
    >
      {profileEditSection === 'personal' && (
        <PersonalInfoEditModal
          embedded
          isOpen
          onSaved={closeProfileEdit}
          onClose={closeProfileEdit}
          initialData={athleteData}
          onUpdate={(updatedProfile) => {
            setAthleteData(prev => prev ? {
              ...prev,
              first_name: updatedProfile.firstName,
              last_name: updatedProfile.lastName,
              email: updatedProfile.email,
              phone_number: updatedProfile.phone,
              date_of_birth: updatedProfile.dateOfBirth,
              graduation_year: updatedProfile.graduationYear,
              city: updatedProfile.city,
              state: updatedProfile.state,
              ncaa_id: updatedProfile.ncaaId,
              x_handle: updatedProfile.xHandle,
              instagram_handle: updatedProfile.instagramHandle,
              featured_video_url: updatedProfile.featuredVideoUrl !== undefined
                ? updatedProfile.featuredVideoUrl
                : (updatedProfile.featured_video_url !== undefined ? updatedProfile.featured_video_url : prev.featured_video_url),
            } : null);
          }}
        />
      )}

      {profileEditSection === 'athletic' && (
        <AthleticInfoEditModal
          embedded
          isOpen
          onSaved={closeProfileEdit}
          onClose={closeProfileEdit}
          initialData={athleticInitialData}
          onUpdate={(updatedData) => {
            setAthleteData(prev => prev ? {
              ...prev,
              sport: updatedData.sport,
              position: updatedData.position,
              height_inches: updatedData.height_inches,
              weight_lbs: updatedData.weight_lbs,
              bats: updatedData.bats,
              throws: updatedData.throws,
              awards: updatedData.awards
            } : null);
          }}
        />
      )}

      {profileEditSection === 'performance' && (
        <PerformanceMetricsEditModal
          embedded
          isOpen
          onSaved={closeProfileEdit}
          onClose={closeProfileEdit}
          initialData={athleteData ? {
            id: athleteData.id,
            stats: athleteData.stats,
          } : { id: '', stats: {} }}
          onUpdate={(updatedData) => {
            setAthleteData(prev => prev ? {
              ...prev,
              stats: {
                ...prev.stats,
                ...updatedData.stats,
              }
            } : null);
          }}
        />
      )}

      {profileEditSection === 'team' && (
        <TeamInfoEditModal
          embedded
          isOpen
          onSaved={closeProfileEdit}
          onClose={closeProfileEdit}
          initialData={teamInitialData}
          athleteId={athleteData?.id || ''}
          onUpdate={() => {
            loadAthleteProfile();
          }}
        />
      )}

      {profileEditSection === 'academic' && (
        detailedAcademicData !== null ? (
          <AcademicInfoEditModal
            embedded
            isOpen
            onSaved={closeProfileEdit}
            onClose={closeProfileEdit}
            initialData={detailedAcademicData}
            athleteId={athleteData?.id || ''}
            onUpdate={() => {
              loadAthleteProfile();
            }}
          />
        ) : (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px',
            color: '#6B7280',
            fontFamily: 'Manrope, sans-serif',
            fontSize: '14px',
          }}>
            Loading academics…
          </div>
        )
      )}

      {profileEditSection === 'awards' && athleteData && (
        <AwardsAndHonorsEditModal
          embedded
          isOpen
          onSaved={closeProfileEdit}
          onClose={closeProfileEdit}
          athleteId={athleteData.id}
          initialAwards={athleteData.awards || []}
          initialAchievements={athleteData.achievements || []}
          onUpdate={() => {
            loadAthleteProfile();
          }}
        />
      )}
    </ProfileEditModal>

    {/* Share Settings Modal */}
    {shareSettingsModalOpen && athleteData && (
      <ShareSettingsModal
        athleteId={athleteData.id}
        athleteName={`${athleteData.first_name} ${athleteData.last_name}`}
        /* Shown under the modal's X and Instagram switch. */
        xHandle={athleteData.x_handle}
        instagramHandle={athleteData.instagram_handle}
        onClose={() => setShareSettingsModalOpen(false)}
      />
    )}

    {/* Calendar Modal */}
    <CalendarModal 
      isOpen={calendarModalOpen} 
      onClose={() => setCalendarModalOpen(false)} 
    />

    {/* Camps Modal */}
    <CampsModal
      isOpen={campsModalOpen}
      onClose={() => setCampsModalOpen(false)}
    />

    {/* Athlete Switch Modal - For Guardians Only */}
    {athleteSwitchModalOpen && (
      <div 
        onClick={() => setAthleteSwitchModalOpen(false)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          fontFamily: 'Manrope, sans-serif'
        }}
      >
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
        >
          {/* Modal Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1F2937',
              margin: 0
            }}>
              Switch Athlete
            </h2>
            <button
              onClick={() => setAthleteSwitchModalOpen(false)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6B7280'
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Modal Content */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px'
          }}>
            <p style={{
              fontSize: '16px',
              color: '#6B7280',
              textAlign: 'center',
              marginBottom: '24px'
            }}>
              Select which athlete profile to view:
            </p>

            {/* Athletes List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {familyAthletes.map((athlete) => {
                const isSelected = athlete.id === athleteData?.id;
                const isDisabled = isSwitchingAthlete || isSelected;
                
                return (
                  <button
                    key={athlete.id}
                    onClick={() => switchAthleteProfile(athlete.id)}
                    disabled={isDisabled}
                    style={{
                      background: isSelected ? 'rgba(249, 85, 13, 0.05)' : 'white',
                      border: isSelected ? '2px solid #F9550D' : '2px solid transparent',
                      borderRadius: '12px',
                      padding: '20px',
                      cursor: isDisabled ? 'default' : 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                      opacity: isDisabled ? 0.7 : 1
                    }}
                    onMouseEnter={(e) => !isDisabled && (e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)')}
                    onMouseLeave={(e) => !isDisabled && (e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)')}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '16px'
                    }}>
                      {/* Athlete Info */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                        {/* Avatar */}
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '50%',
                          background: '#F3F4F6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#9CA3AF',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}>
                          {athlete.profilePictureUrl ? (
                            <img 
                              src={athlete.profilePictureUrl} 
                              alt={`${athlete.firstName} ${athlete.lastName}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <span>{athlete.firstName?.[0]}{athlete.lastName?.[0]}</span>
                          )}
                        </div>

                        {/* Name and Details */}
                        <div style={{ flex: 1, textAlign: 'left' }}>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#1F2937',
                            marginBottom: '8px'
                          }}>
                            {athlete.firstName} {athlete.lastName}
                          </div>
                          
                          {/* Details Grid */}
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '12px',
                            fontSize: '13px',
                            color: '#6B7280'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Activity size={14} color="#F9550D" />
                              <span>{athlete.sportInfo?.sport || athlete.sport || 'Sport'}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <MapPin size={14} color="#F9550D" />
                              <span>
                                {Array.isArray(athlete.sportInfo?.positions) 
                                  ? athlete.sportInfo.positions[0] 
                                  : athlete.sportInfo?.positions?.split(',')[0] || athlete.position || 'Position'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Calendar size={14} color="#F9550D" />
                              <span>Class of {athlete.graduationYear}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right Side - Badge or Arrow */}
                      <div style={{ flexShrink: 0 }}>
                        {isSelected ? (
                          <div style={{
                            background: '#F9550D',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '600',
                            padding: '4px 12px',
                            borderRadius: '12px'
                          }}>
                            Current
                          </div>
                        ) : isSwitchingAthlete ? (
                          <div style={{ 
                            border: '2px solid rgba(249, 85, 13, 0.3)',
                            borderTop: '2px solid #F9550D',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            animation: 'spin 1s linear infinite'
                          }} />
                        ) : (
                          <ChevronRight size={20} color="#9CA3AF" />
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Coaches List Modal */}
    {coachesModalOpen && (
      <div 
        onClick={() => setCoachesModalOpen(false)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          fontFamily: 'Manrope, sans-serif'
        }}
      >
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
        >
          {/* Modal Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1F2937',
              margin: 0
            }}>
              {coachesModalTitle}
            </h2>
            <button
              aria-label="Close"
              onClick={() => setCoachesModalOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6B7280',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Coaches List */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px'
          }}>
            {selectedCoaches.map((coach: any, index: number) => (
              <div 
                key={index}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '20px',
                  marginBottom: '16px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}
              >
                {/* Coach Avatar and Name Section */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '30px',
                    backgroundColor: '#F9550D',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <span style={{
                      fontSize: '20px',
                      color: 'white',
                      fontWeight: '600'
                    }}>
                      {coach.name ? coach.name.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'C'}
                    </span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#1F2937',
                      marginBottom: '4px'
                    }}>
                      {coach.name}
                    </div>
                    {coach.role?.toLowerCase() === 'head coach' ? (
                      <div style={{
                        display: 'inline-block',
                        backgroundColor: '#F9550D',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        borderRadius: '16px'
                      }}>
                        <span style={{
                          fontSize: '12px',
                          color: 'white',
                          fontWeight: '600'
                        }}>
                          Head Coach
                        </span>
                      </div>
                    ) : (
                      <div style={{
                        fontSize: '16px',
                        color: '#6B7280',
                        textTransform: 'capitalize'
                      }}>
                        {coach.role || 'Assistant Coach'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Coach Details Section */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '16px'
                }}>
                  {/* Email */}
                  <div>
                    <div style={{
                      fontSize: '12px',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '4px'
                    }}>
                      EMAIL
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#1F2937',
                      fontWeight: '500',
                      wordBreak: 'break-word'
                    }}>
                      {coach.email || 'Not provided'}
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <div style={{
                      fontSize: '12px',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '4px'
                    }}>
                      PHONE
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#1F2937',
                      fontWeight: '500'
                    }}>
                      {coach.phone || 'Not provided'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )}
  </>
  );
};

// Helper Components
const SectionHeader: React.FC<{ title: string; onEdit?: () => void }> = ({ title, onEdit }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  }}>
    <h3 style={{
      fontSize: '18px',
      fontWeight: '600',
      color: '#1F2937',
      margin: 0
    }}>
      {title}
    </h3>
    {onEdit && (
      <button
        onClick={onEdit}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'transparent',
          border: 'none',
          color: '#F9550D',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          padding: '4px 8px',
          fontFamily: 'Manrope, sans-serif'
        }}
      >
        <Edit2 size={16} aria-label="Edit" />
        Edit
      </button>
    )}
  </div>
);

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '8px',
    paddingBottom: '8px'
  }}>
    <span style={{ fontSize: '14px', color: '#6B7280' }}>{label}</span>
    <span style={{ fontSize: '14px', fontWeight: '500', color: '#1F2937', textAlign: 'right' }}>{value}</span>
  </div>
);

const MetricItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>{value}</div>
  </div>
);

const TeamInfo: React.FC<{ teamName: string; headCoach?: any; teamLevel?: string }> = ({ teamName, headCoach, teamLevel }) => (
  <div style={{
    padding: '12px',
    background: '#F9FAFB',
    borderRadius: '8px'
  }}>
    <div style={{ marginBottom: '8px' }}>
      <span style={{ fontSize: '13px', color: '#6B7280' }}>Team: </span>
      <span style={{ fontSize: '14px', fontWeight: '500', color: '#1F2937' }}>{teamName}</span>
    </div>
    {headCoach && (
      <>
        <div style={{ marginBottom: '4px' }}>
          <span style={{ fontSize: '13px', color: '#6B7280' }}>Coach: </span>
          <span style={{ fontSize: '14px', fontWeight: '500', color: '#1F2937' }}>{headCoach.name}</span>
        </div>
        {teamLevel && (
          <div style={{ marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', color: '#6B7280' }}>Level: </span>
            <span style={{ fontSize: '14px', color: '#1F2937' }}>{teamLevel}</span>
          </div>
        )}
        <div style={{ marginBottom: '4px' }}>
          <span style={{ fontSize: '13px', color: '#6B7280' }}>Email: </span>
          <span style={{ fontSize: '14px', color: '#1F2937' }}>{headCoach.email}</span>
        </div>
        {headCoach.phone && (
          <div>
            <span style={{ fontSize: '13px', color: '#6B7280' }}>Phone: </span>
            <span style={{ fontSize: '14px', color: '#1F2937' }}>{headCoach.phone}</span>
          </div>
        )}
      </>
    )}
  </div>
);

export default PlayerProfile;
